import { useState, useContext } from 'react';
import Button from '@/components/Button';
import Ellipsis from '@/components/icons/Ellipsis';
import SupportLink from '@/components/SupportLink';
import ReplaceProviderButtonV2 from './ReplaceProviderButtonV2';
import { ProviderWithFirstAppointmentInfo, TherapyType } from '@/utils/types';
import { useNavigate } from 'react-router-dom';
import { MobileContext, StateContext } from '@/App';
import Text from '@/components/Text';
import trackEvent from '@/utils/amplitude';
import useData from '@/state/use-data';
import actions from '@/state/actions';
import { isOfferingInPerson } from '@/api/provider.api';
import { useFlagCheck } from '@/utils/use-feature-flags';
import ChooseAppointmentLocationModal from '@/components/ChooseAppointmentLocationModal';
import { isLoaded } from '@/utils/types';
import { isBookingAllowed } from '@/utils/use-dns-flag';
import WithDisabledTooltip from '@/components/WithDisabledTooltip';

interface Props {
  provider: ProviderWithFirstAppointmentInfo;
  therapyType: TherapyType;
  active: boolean;
  canBook?: boolean;
}

const ProviderCardActions = ({
  provider,
  therapyType,
  active,
  canBook = false,
}: Props) => {
  const { dispatch } = useContext(StateContext);
  const mobile = useContext(MobileContext);
  const { WithData, data } = useData(['profile', 'patientData']);
  const { patientData } = data;
  const patientUuid = isLoaded(patientData) ? patientData.patient_uuid : '';
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAllowed } = useFlagCheck();
  const inPersonEnabled = isAllowed({
    patientPortalReleaseInPersonVisits: true,
  });
  const bookingAllowed = isBookingAllowed();

  const navigateToReschedule = () => {
    navigate(
      `/appointments/schedule-follow-up/${provider.npi}/${therapyType}${
        !active ? '?reactivate=true' : ''
      }`,
    );
  };

  const handleBookClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackEvent({
      product_area: 'CareTeam',
      name: 'book_appointment_clicked',
      trigger: 'Interaction',
    });
    if (inPersonEnabled) {
      const offersInPerson = await isOfferingInPerson(
        provider.uuid,
        patientUuid,
      );
      if (offersInPerson && !active && therapyType !== 'psychiatric') {
        dispatch(
          actions.setModal({
            children: (
              <ChooseAppointmentLocationModal
                onTelemedicineSelect={() => navigateToReschedule()}
                appointmentLocationPath="reactivation"
              />
            ),
          }),
        );
        return;
      }
    }
    navigateToReschedule();
  };

  const getMenuClassName = () => {
    const classNames = [
      'absolute',
      'flex',
      'flex-col',
      'items-start',
      'cursor-pointer',
      'gap-6',
      'p-4',
      'top-11',
      'w-[188px]',
      'bg-white',
      'z-10',
      'rounded',
      'shadow-lg',
      'right-0',
      'xl:right-[unset]',
      'xl:left-0',
    ];
    if (!bookingAllowed) {
      classNames.push('text-grey-300');
    }
    return classNames.join(' ');
  };

  if (!active) {
    return (
      <WithData data={data}>
        {({ profile }) => {
          const { location } = profile;
          if (
            provider.status === 'live' &&
            provider.two_letter_state === location
          ) {
            return (
              <WithDisabledTooltip
                enabled={!bookingAllowed}
                info={
                  <span>
                    Your account has been restricted from scheduling{' '}
                    appointments. Contact <SupportLink />
                  </span>
                }
              >
                <Button
                  size="medium"
                  variant="primary-outline"
                  onClick={(e: React.MouseEvent) => handleBookClick(e)}
                  state={!bookingAllowed ? 'disabled' : undefined}
                >
                  {mobile ? 'Book' : 'Book appointment'}
                </Button>
              </WithDisabledTooltip>
            );
          } else {
            return null; // Should be showing ProviderUnavailableNotice in a parent component
          }
        }}
      </WithData>
    );
  }

  return (
    <div
      className="relative"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setMobileMenuOpen(!mobileMenuOpen);
      }}
    >
      <Button
        size="small"
        data-testid="overflow-menu"
        customClasses={mobile ? 'items-center min-h-[42px]' : undefined}
      >
        <Ellipsis />
      </Button>
      {mobileMenuOpen ? (
        <div
          onClickCapture={(e) => {
            if (!bookingAllowed) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          className={getMenuClassName()}
        >
          {canBook ? (
            <Text.P
              role="button"
              onClick={(e: React.MouseEvent) => handleBookClick(e)}
            >
              Book appointment
            </Text.P>
          ) : null}
          <ReplaceProviderButtonV2
            providerNpi={provider.npi}
            therapyType={therapyType}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ProviderCardActions;
